/**
 * Calculadora de Presupuesto Óptico
 * Permite calcular la viabilidad de enlaces de fibra óptica
 * @module components/forms/OpticalBudgetCalculator
 */

import { useState, useEffect } from 'react'
import { Calculator, AlertTriangle, CheckCircle, Zap, Settings, RefreshCw } from 'lucide-react'
import {
  calcularPresupuestoOptico,
  obtenerEspecificacionesGPON,
  obtenerEspecificacionesXGSPON,
  calcularDistanciaMaxima,
  OpticalBudgetInput,
  OpticalBudgetResult,
  SPLITTER_RATIOS,
  SPLITTER_RATIOS_DESBALANCEADO,
} from '@/services/opticalBudgetService'

type Tecnologia = 'GPON' | 'XGS-PON' | 'custom'

interface OpticalBudgetCalculatorProps {
  distanciaInicial?: number
  empalmesInicial?: number
  conectoresInicial?: number
  splitterInicial?: string
  onResultChange?: (result: OpticalBudgetResult | null) => void
}

export function OpticalBudgetCalculator({
  distanciaInicial = 5,
  empalmesInicial = 4,
  conectoresInicial = 2,
  splitterInicial = '1:8',
  onResultChange,
}: OpticalBudgetCalculatorProps) {
  const [tecnologia, setTecnologia] = useState<Tecnologia>('GPON')
  const [potenciaTx, setPotenciaTx] = useState(3.0)
  const [sensibilidadRx, setSensibilidadRx] = useState(-28.0)
  const [margenDiseno, setMargenDiseno] = useState(3.0)
  const [distanciaKm, setDistanciaKm] = useState(distanciaInicial)
  const [cantidadEmpalmes, setCantidadEmpalmes] = useState(empalmesInicial)
  const [cantidadConectores, setCantidadConectores] = useState(conectoresInicial)
  const [ratioSplitter, setRatioSplitter] = useState<string | null>(splitterInicial)
  const [esDesbalanceado, setEsDesbalanceado] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const [resultado, setResultado] = useState<OpticalBudgetResult | null>(null)

  useEffect(() => {
    if (tecnologia === 'GPON') {
      const specs = obtenerEspecificacionesGPON()
      setPotenciaTx(3.0)
      setSensibilidadRx(specs.sensibilidadRx)
    } else if (tecnologia === 'XGS-PON') {
      const specs = obtenerEspecificacionesXGSPON()
      setPotenciaTx(4.0)
      setSensibilidadRx(specs.sensibilidadRx)
    }
  }, [tecnologia])

  useEffect(() => {
    const input: OpticalBudgetInput = {
      potenciaTx,
      sensibilidadRx,
      margenDiseno,
      distanciaKm,
      cantidadEmpalmes,
      cantidadConectores,
      ratioSplitter,
      esDesbalanceado,
    }

    const result = calcularPresupuestoOptico(input)
    setResultado(result)
    onResultChange?.(result)
  }, [
    potenciaTx,
    sensibilidadRx,
    margenDiseno,
    distanciaKm,
    cantidadEmpalmes,
    cantidadConectores,
    ratioSplitter,
    esDesbalanceado,
    onResultChange,
  ])

  const distanciaMaxima = calcularDistanciaMaxima(
    potenciaTx - sensibilidadRx - margenDiseno,
    cantidadEmpalmes,
    cantidadConectores,
    ratioSplitter
  )

  const resetValues = () => {
    setDistanciaKm(distanciaInicial)
    setCantidadEmpalmes(empalmesInicial)
    setCantidadConectores(conectoresInicial)
    setRatioSplitter(splitterInicial)
    setEsDesbalanceado(false)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="font-medium flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Calculadora de Presupuesto Óptico
        </h4>
        <button
          type="button"
          onClick={resetValues}
          className="text-sm px-3 py-1 text-gray-600 hover:bg-gray-100 dark:hover:bg-dark-700 rounded flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" /> Reset
        </button>
      </div>

      {/* Tecnología */}
      <div>
        <label className="block text-sm font-medium mb-2">Tecnología PON</label>
        <div className="flex gap-2">
          {(['GPON', 'XGS-PON', 'custom'] as Tecnologia[]).map((tech) => (
            <button
              key={tech}
              type="button"
              onClick={() => setTecnologia(tech)}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                tecnologia === tech
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'border-gray-300 dark:border-dark-600 hover:border-blue-300'
              }`}
            >
              {tech === 'custom' ? 'Personalizado' : tech}
            </button>
          ))}
        </div>
      </div>

      {/* Parámetros del enlace */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">
            Distancia (km)
          </label>
          <input
            type="number"
            value={distanciaKm}
            onChange={(e) => setDistanciaKm(parseFloat(e.target.value) || 0)}
            step="0.1"
            min="0"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            Máx. recomendado: {distanciaMaxima.toFixed(1)} km
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Cantidad de Empalmes
          </label>
          <input
            type="number"
            value={cantidadEmpalmes}
            onChange={(e) => setCantidadEmpalmes(parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            Pérdida: 0.1 dB/empalme (fusión)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Cantidad de Conectores
          </label>
          <input
            type="number"
            value={cantidadConectores}
            onChange={(e) => setCantidadConectores(parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            Pérdida: 0.5 dB/conector
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Ratio Splitter
          </label>
          <div className="flex gap-2">
            <select
              value={ratioSplitter || ''}
              onChange={(e) => setRatioSplitter(e.target.value || null)}
              className="flex-1 px-3 py-2 border rounded-lg dark:bg-dark-700 dark:border-dark-600"
            >
              <option value="">Sin splitter</option>
              <optgroup label="Balanceado">
                {SPLITTER_RATIOS.map((ratio) => (
                  <option key={ratio} value={ratio}>{ratio}</option>
                ))}
              </optgroup>
              {esDesbalanceado && (
                <optgroup label="Desbalanceado">
                  {SPLITTER_RATIOS_DESBALANCEADO.map((ratio) => (
                    <option key={ratio} value={ratio}>{ratio}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <button
              type="button"
              onClick={() => setEsDesbalanceado(!esDesbalanceado)}
              className={`px-3 py-2 rounded-lg border text-xs ${
                esDesbalanceado
                  ? 'border-orange-500 bg-orange-50 text-orange-700 dark:bg-orange-900/20'
                  : 'border-gray-300 dark:border-dark-600'
              }`}
              title="Habilitar ratios desbalanceados"
            >
              ⚖️
            </button>
          </div>
        </div>
      </div>

      {/* Configuración avanzada */}
      <div className="border rounded-lg dark:border-dark-600">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-700 rounded-lg"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            <span className="text-sm font-medium">Configuración Avanzada</span>
          </div>
          <span>{showAdvanced ? '▼' : '▶'}</span>
        </button>

        {showAdvanced && (
          <div className="p-4 border-t dark:border-dark-600 grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1">
                Potencia Tx (dBm)
              </label>
              <input
                type="number"
                value={potenciaTx}
                onChange={(e) => setPotenciaTx(parseFloat(e.target.value) || 0)}
                step="0.5"
                disabled={tecnologia !== 'custom'}
                className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                Sensibilidad Rx (dBm)
              </label>
              <input
                type="number"
                value={sensibilidadRx}
                onChange={(e) => setSensibilidadRx(parseFloat(e.target.value) || 0)}
                step="0.5"
                disabled={tecnologia !== 'custom'}
                className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-dark-700 dark:border-dark-600 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">
                Margen de Diseño (dB)
              </label>
              <input
                type="number"
                value={margenDiseno}
                onChange={(e) => setMargenDiseno(parseFloat(e.target.value) || 0)}
                step="0.5"
                min="0"
                className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-dark-700 dark:border-dark-600"
              />
            </div>
          </div>
        )}
      </div>

      {/* Resultados */}
      {resultado && (
        <div className={`rounded-lg p-4 border ${
          resultado.esViable
            ? 'bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700'
            : 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            {resultado.esViable ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-600" />
                <span className="font-bold text-green-700 dark:text-green-400">
                  Enlace VIABLE
                </span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-6 h-6 text-red-600" />
                <span className="font-bold text-red-700 dark:text-red-400">
                  Enlace NO VIABLE
                </span>
              </>
            )}
          </div>

          {/* Desglose de pérdidas */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between text-sm">
              <span>Presupuesto disponible:</span>
              <span className="font-medium">{resultado.presupuestoDisponible.toFixed(2)} dB</span>
            </div>
            <div className="border-t dark:border-dark-600 pt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Pérdida fibra ({distanciaKm} km × 0.35 dB/km):
                </span>
                <span className="text-orange-600">-{resultado.perdidaFibra.toFixed(2)} dB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Pérdida empalmes ({cantidadEmpalmes} × 0.1 dB):
                </span>
                <span className="text-orange-600">-{resultado.perdidaEmpalmes.toFixed(2)} dB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">
                  Pérdida conectores ({cantidadConectores} × 0.5 dB):
                </span>
                <span className="text-orange-600">-{resultado.perdidaConectores.toFixed(2)} dB</span>
              </div>
              {ratioSplitter && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    Pérdida splitter ({ratioSplitter}):
                  </span>
                  <span className="text-orange-600">-{resultado.perdidaSplitter.toFixed(2)} dB</span>
                </div>
              )}
            </div>
            <div className="border-t dark:border-dark-600 pt-2 flex justify-between text-sm font-bold">
              <span>Pérdida TOTAL:</span>
              <span className="text-orange-600">-{resultado.perdidaTotal.toFixed(2)} dB</span>
            </div>
            <div className={`flex justify-between text-sm font-bold ${
              resultado.margenRestante > 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              <span>Margen restante:</span>
              <span>{resultado.margenRestante.toFixed(2)} dB</span>
            </div>
          </div>

          {/* Barra visual */}
          <div className="mb-4">
            <div className="h-4 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all ${
                  resultado.esViable ? 'bg-green-500' : 'bg-red-500'
                }`}
                style={{ 
                  width: `${Math.min(100, (resultado.perdidaTotal / resultado.presupuestoDisponible) * 100)}%` 
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0 dB</span>
              <span>Pérdida: {((resultado.perdidaTotal / resultado.presupuestoDisponible) * 100).toFixed(0)}%</span>
              <span>{resultado.presupuestoDisponible.toFixed(1)} dB</span>
            </div>
          </div>

          {/* Advertencias */}
          {resultado.advertencias.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Advertencias</p>
                  <ul className="text-xs text-yellow-700 dark:text-yellow-300 list-disc list-inside mt-1">
                    {resultado.advertencias.map((adv, idx) => (
                      <li key={idx}>{adv}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Información */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Zap className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <p className="font-medium mb-1">Valores típicos {tecnologia}:</p>
            <ul className="space-y-0.5">
              <li>• Potencia OLT: +1.5 a +5 dBm</li>
              <li>• Sensibilidad ONT: -28 dBm</li>
              <li>• Presupuesto Clase B+: 28 dB</li>
              <li>• Distancia máxima: 20 km</li>
              <li>• Split máximo recomendado: 1:64</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
